import {
  Card,
  InputField,
  Inspector,
  List,
  Tooltip,
} from '@bynder/design-system';
import { useActiveWidget } from '../../../../queries/guides';
import { WIDGET_TYPE, YoutubeWidget } from '../../../../types';
import { WidgetSettingsHeader } from '../../Styles';
import { useEffect, useState } from 'react';
import { useYoutubeWidgetMutations } from '../../../../mutations/widgets/youtube';
import { useTranslate } from '@bynder/localization';
import { useQuery } from '@tanstack/react-query';
import { IconDelete, IconInfo, IconYoutube } from '@bynder/icons';
import MediaBorderRadius from '../MediaBorderRadius';
import { emptyCSSObject } from '../../../../constants/widgets';
import { isDevelopment } from '../../../../constants/app';
import CustomCssStyleEditor from '../CustomCSSEditor';

export const YoutubeWidgetSettings = () => {
  const { translate } = useTranslate();
  const youtubeWidget = useActiveWidget() as YoutubeWidget;
  const { editSource, editProperties, editStyle } = useYoutubeWidgetMutations();
  const [videoUrl, setVideoUrl] = useState<string>(
    youtubeWidget?.sourceId
      ? `http://www.youtube.com/watch?v=${youtubeWidget?.sourceId}`
      : '',
  );

  const onAddUrl = (url: string) => {
    setVideoUrl(url);
    const videoId = extractVideoID(url);
    if (videoId) {
      editSource.mutate({ source: extractVideoID(url) });
    }
  };

  const handleEditStyle = (style: string) => {
    editStyle.mutate({ style });
  };

  useEffect(() => {
    setVideoUrl(
      youtubeWidget?.sourceId
        ? `http://www.youtube.com/watch?v=${youtubeWidget?.sourceId}`
        : '',
    );
  }, [youtubeWidget.sourceId]);

  return (
    <>
      <WidgetSettingsHeader widgetType={WIDGET_TYPE.YOUTUBE} />
      <List>
        {youtubeWidget.sourceId && (
          <YoutubeWidgetCard videoId={youtubeWidget.sourceId} />
        )}
        <InputField
          isInvalid={extractVideoID(videoUrl) === ''}
          helperText={extractVideoID(videoUrl) === '' && 'Invalid URL'}
          onChange={onAddUrl}
          value={videoUrl}
          label="YouTube URL"
          placeholder="https://www.youtube.com/watch?v=dQw4w9WgXcQ"
        />
        <List.Item
          isDisabled={youtubeWidget.sourceId === ''}
          checkedVariant="switch"
          isChecked={youtubeWidget.settings.controls}
          onClick={() =>
            editProperties.mutate({
              property: { controls: !youtubeWidget.settings.controls },
            })
          }
        >
          {translate('WIDGET_INSPECTOR.VIDEO_CONTROLS_TOGGLE')}
        </List.Item>
        <List.Item
          isDisabled={youtubeWidget.sourceId === ''}
          isChecked={youtubeWidget.settings.autoplay}
          checkedVariant="switch"
          onClick={() =>
            editProperties.mutate({
              property: { autoplay: !youtubeWidget.settings.autoplay },
            })
          }
        >
          <Tooltip
            content={translate('WIDGET_INSPECTOR.VIDEO_AUTPLAY_MUTED_TOOLTIP')}
            timing="delay"
          >
            {translate('WIDGET_INSPECTOR.VIDEO_AUTOPLAY_TOGGLE')} <IconInfo />
          </Tooltip>
        </List.Item>
        <List.Item
          isDisabled={youtubeWidget.sourceId === ''}
          isChecked={youtubeWidget.settings.loop}
          checkedVariant="switch"
          onClick={() =>
            editProperties.mutate({
              property: { loop: !youtubeWidget.settings.loop },
            })
          }
        >
          {translate('WIDGET_INSPECTOR.VIDEO_LOOP_TOGGLE')}
        </List.Item>
        <Inspector.Divider />
        <MediaBorderRadius
          style={youtubeWidget?.style?.stylesheet ?? emptyCSSObject.stylesheet}
          onEditStyle={handleEditStyle}
        />

        {isDevelopment && (
          <>
            <Inspector.Divider />
            <CustomCssStyleEditor
              handleChange={() => editStyle.mutate({ style: '' })}
              label="Section CSS"
              placeholder="Enter your custom CSS here"
              style={youtubeWidget?.style?.stylesheet}
            />
          </>
        )}
      </List>
    </>
  );
};

function extractVideoID(input) {
  // Check if the provided input is a valid YouTube video ID
  if (/^[a-zA-Z0-9_-]{11}$/.test(input)) {
    return input;
  }

  // Regular expression to match YouTube video URLs
  const youtubeUrlRegex =
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const youtubeShortsRegex =
    /https?:\/\/www\.youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/;

  const match = input.match(youtubeUrlRegex);
  const shortsMatch = input.match(youtubeShortsRegex);

  if (match && match[1]) {
    return match[1];
  }

  if (shortsMatch && shortsMatch[1]) {
    return shortsMatch[1];
  }

  // If no valid ID found, return null
  return '';
}

const YoutubeWidgetCard = ({ videoId }: { videoId: string }) => {
  const { editSource } = useYoutubeWidgetMutations();
  const { translate } = useTranslate();
  const clearVideo = () => {
    editSource.mutate({ source: '' });
  };

  const { data, isLoading } = useQuery({
    enabled: !!videoId,
    queryKey: [videoId],
    queryFn: () =>
      fetch(
        `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}`,
      ).then(res => res.json()),
  });

  if (isLoading) return null;

  return (
    <Card
      variant="clean"
      contextAction={
        <Card.ContextActionButton
          tooltip={translate('WIDGET_INSPECTOR.CLEAR_VIDEO_BUTTON')}
          aria-label="Clear video"
          onClick={clearVideo}
        >
          <IconDelete />
        </Card.ContextActionButton>
      }
    >
      <Card.Frame>
        {data?.thumbnail_url ? (
          <Card.Images>
            <Card.Img contentResize="fit" src={data.thumbnail_url} />
          </Card.Images>
        ) : (
          <Card.IconPlaceholder>
            <IconYoutube aria-label="youtubeIcon" />
          </Card.IconPlaceholder>
        )}
      </Card.Frame>

      <Card.Header title={data?.title ?? ''} />
    </Card>
  );
};
